/**
 * HTML tables in section text → structured tables Word can build.
 *
 * Library text carries its tables as HTML (`<table><tr><th>…`). The server
 * rewrites them as pipe tables before a copy is proposed
 * (`server/html_tables.py`), but any `<table>` that still reaches the
 * formatter would otherwise land in Word as literal tags. Here each one is
 * lifted out of the text and parsed into a `StructuredTableData`: `colspan` →
 * `grid_span`, `rowspan` → a `v_merge` restart cell with continue cells in the
 * rows below, `<th>` → bold, `<br>` / `<p>` → a line break in the cell. The
 * structured-table renderer then inserts it with its merges intact.
 *
 * Pure: no DOM, no Office JS.
 */

import type { StructuredTableCell, StructuredTableData } from "./types";

const RE_HTML_TABLE = /<table\b[^>]*>[\s\S]*?<\/table\s*>/gi;
const RE_TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^>]*?)(\/?)>|([^<]+)/g;
const LINE_BREAK_TAGS = new Set(["br", "p", "div", "li"]);

/** The line a lifted table leaves in the text: `parseMarkdown` turns it back
 *  into the table at that position. Delimited by a private-use character no real text carries. */
export const HTML_TABLE_SENTINEL = /^\uE000HTMLTABLE(\d+)\uE000$/;

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  times: "×",
  plusmn: "±",
  deg: "°",
  micro: "µ",
  mu: "μ",
  le: "≤",
  ge: "≥",
  ndash: "–",
  mdash: "—",
  middot: "·",
  reg: "®",
  trade: "™",
  copy: "©",
};

export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, name: string) => {
    if (name[0] === "#") {
      const code = name[1] === "x" || name[1] === "X" ? Number.parseInt(name.slice(2), 16) : Number(name.slice(1));
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[name.toLowerCase()] ?? whole;
  });
}

export function hasHtmlTable(text: string): boolean {
  return /<table\b[^>]*>[\s\S]*?<\/table\s*>/i.test(text);
}

function spanAttr(attrs: string, name: string): number {
  const m = new RegExp(`\\b${name}\\s*=\\s*["']?(\\d+)`, "i").exec(attrs);
  const n = m ? Number(m[1]) : 1;
  return Math.max(1, Math.min(Number.isFinite(n) ? n : 1, 50));
}

interface RawCell {
  text: string;
  colspan: number;
  rowspan: number;
  header: boolean;
}

/** Rows of cells from one `<table>…</table>`; a table nested in a cell is
 *  read as lines of that cell. */
function readRows(html: string): RawCell[][] {
  const rows: RawCell[][] = [];
  let cell: RawCell | null = null;
  let depth = 0;
  const push = (s: string) => {
    if (cell) cell.text += s;
  };
  for (const m of html.matchAll(RE_TOKEN)) {
    const [, closing, rawTag, attrs = "", , data] = m;
    if (data !== undefined) {
      push(data);
      continue;
    }
    const tag = rawTag.toLowerCase();
    if (tag === "table") {
      depth += closing ? -1 : 1;
      continue;
    }
    if (depth > 1) {
      if (!closing && (tag === "tr" || tag === "br" || tag === "p")) push("\n");
      else if (!closing && (tag === "td" || tag === "th")) push(" ");
      continue;
    }
    if (tag === "tr" && !closing) {
      rows.push([]);
    } else if ((tag === "td" || tag === "th") && !closing) {
      if (rows.length === 0) rows.push([]);
      cell = {
        text: "",
        colspan: spanAttr(attrs, "colspan"),
        rowspan: spanAttr(attrs, "rowspan"),
        header: tag === "th",
      };
      rows[rows.length - 1].push(cell);
    } else if ((tag === "td" || tag === "th") && closing) {
      cell = null;
    } else if (LINE_BREAK_TAGS.has(tag)) {
      push("\n");
    }
  }
  return rows;
}

function cellText(raw: string): string {
  return decodeEntities(raw)
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
}

/** One HTML table as a structured table, or null when it has no cells. */
export function parseHtmlTable(html: string): StructuredTableData | null {
  const raw = readRows(html).filter((r) => r.length > 0);
  if (raw.length === 0) return null;
  // Slots already owned by a rowspan from above: row → col → the owning
  // cell's colspan (its continue cell spans the same columns).
  const owned = new Map<number, Map<number, number>>();
  const rows = raw.map((cells, r) => {
    const out: Array<{ col: number; cell: StructuredTableCell }> = [];
    const mine = owned.get(r) ?? new Map<number, number>();
    for (const [col, span] of mine) out.push({ col, cell: { text: "", grid_span: span, v_merge: "continue" } });
    let col = 0;
    for (const c of cells) {
      while ([...mine].some(([start, span]) => col >= start && col < start + span)) col++;
      const cell: StructuredTableCell = { text: cellText(c.text), grid_span: c.colspan };
      if (c.header) cell.bold = true;
      if (c.rowspan > 1) {
        cell.v_merge = "restart";
        for (let rr = r + 1; rr < Math.min(r + c.rowspan, raw.length); rr++) {
          if (!owned.has(rr)) owned.set(rr, new Map());
          owned.get(rr)?.set(col, c.colspan);
        }
      }
      out.push({ col, cell });
      col += c.colspan;
    }
    return { cells: out.sort((a, b) => a.col - b.col).map((x) => x.cell) };
  });
  return { rows };
}

/**
 * Lift every HTML table out of `raw`: each becomes a sentinel line (on its own,
 * set off by blank lines) and its parsed data, in order. Text without a table
 * comes back unchanged with no tables.
 */
export function extractHtmlTables(raw: string): { text: string; tables: StructuredTableData[] } {
  if (!hasHtmlTable(raw)) return { text: raw, tables: [] };
  const tables: StructuredTableData[] = [];
  const text = raw.replace(RE_HTML_TABLE, (html) => {
    const data = parseHtmlTable(html);
    if (!data) return "\n\n";
    tables.push(data);
    return `\n\n\uE000HTMLTABLE${tables.length - 1}\uE000\n\n`;
  });
  return { text, tables };
}

/**
 * Every HTML table in `raw` as pipe-table rows, for a word diff: what a
 * reader compares is the cells, not the tags. A merged cell's text sits in
 * its first slot, the rest of its span is blank; line breaks read as spaces.
 */
export function htmlTablesAsText(raw: string): string {
  if (!hasHtmlTable(raw)) return raw;
  return raw.replace(RE_HTML_TABLE, (html) => {
    const data = parseHtmlTable(html);
    if (!data) return "";
    const lines = data.rows.map(
      (row) =>
        `| ${(row.cells ?? [])
          .map((c) => {
            const text = c.v_merge === "continue" ? "" : (c.text ?? "").replace(/\n/g, " ").replace(/\|/g, "\\|");
            return [text, ...Array<string>(Math.max(0, (c.grid_span ?? 1) - 1)).fill("")].join(" | ");
          })
          .join(" | ")} |`,
    );
    return `\n\n${lines.join("\n")}\n\n`;
  });
}
